import React from "react";
import { useTranslation } from "react-i18next";
import { Ban } from "lucide-react";
import { Button, Dialog } from "../../../pages/manage/ui";

// What blocking / unblocking does, as a short list under the dialog text.
const Consequences = ({ heading, items }) => (
  <div>
    <p className="tw-mb-2 tw-text-[15px] tw-font-medium">{heading}</p>
    <ul className="tw-space-y-1.5">
      {items.map((text) => (
        <li key={text} className="tw-flex tw-items-start tw-gap-2.5 tw-text-sm tw-text-muted-foreground">
          <span className="tw-mt-[7px] tw-size-1.5 tw-shrink-0 tw-rounded-full tw-bg-[var(--fill-stronger)]" />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  </div>
);

export function BlockUserConfirmationModal({ open, onClose, userName, onConfirm, isBlocking }) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={!isBlocking ? onClose : undefined}
      hideClose={isBlocking}
      closeOnBackdrop={false}
      title={userName ? t("account_block_name", { name: userName, defaultValue: "Block {{name}}?" }) : t("block_user", { defaultValue: "Block user" })}
      description={t("block_user_warning", { defaultValue: "Blocking this user will prevent all interactions between you and them." })}
      footer={
        <>
          <Button variant="light" onClick={onClose} disabled={isBlocking}>
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button variant="destructive" onClick={onConfirm} loading={isBlocking}>
            {!isBlocking && <Ban />}
            {isBlocking ? t("blocking", { defaultValue: "Blocking..." }) : t("block_user", { defaultValue: "Block user" })}
          </Button>
        </>
      }
    >
      <Consequences
        heading={t("what_happens_when_block", { defaultValue: "What happens when you block someone:" })}
        items={[
          t("block_consequence_1", { defaultValue: "They won't be able to see your profile or posts" }),
          t("block_consequence_2", { defaultValue: "They won't be able to message you" }),
          t("block_consequence_3", { defaultValue: "You'll both be unfollowed from each other" }),
          t("block_consequence_4", { defaultValue: "They won't be notified that you blocked them" }),
        ]}
      />
    </Dialog>
  );
}

export function UnblockUserConfirmationModal({ open, onClose, userName, onConfirm, isUnblocking }) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={!isUnblocking ? onClose : undefined}
      hideClose={isUnblocking}
      closeOnBackdrop={false}
      title={userName ? t("account_unblock_name", { name: userName, defaultValue: "Unblock {{name}}?" }) : t("unblock_user", { defaultValue: "Unblock user" })}
      description={t("unblock_user_warning", { defaultValue: "This user will be able to interact with you again." })}
      footer={
        <>
          <Button variant="light" onClick={onClose} disabled={isUnblocking}>
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button onClick={onConfirm} loading={isUnblocking}>
            {isUnblocking ? t("unblocking", { defaultValue: "Unblocking..." }) : t("unblock", { defaultValue: "Unblock" })}
          </Button>
        </>
      }
    >
      <Consequences
        heading={t("what_happens_when_unblock", { defaultValue: "What happens when you unblock someone:" })}
        items={[
          t("unblock_consequence_1", { defaultValue: "They'll be able to see your profile and posts" }),
          t("unblock_consequence_2", { defaultValue: "They'll be able to message you" }),
          t("unblock_consequence_3", { defaultValue: "They'll be able to follow you again" }),
          t("unblock_consequence_4", { defaultValue: "They won't be notified that you unblocked them" }),
        ]}
      />
    </Dialog>
  );
}
